// shouldRequirePasskey is the SPA-side mirror of the backend's
// RequirePasskeyFactor middleware. The bug it gates: a fresh signup
// (or magic-link login) can end up on the tenant subdomain via direct
// navigation (refresh-when-DNS-flips-live) instead of through the
// SetupDoneView "Til dashboard" button. Without this guard the
// dashboard chrome renders, every API call 403s silently, and the
// operator is stuck with "ukjent" / empty states and no clear path.
//
// Three-way routing (#1272). When a non-passkey session hits a passkey-
// gated path:
//   - hasPasskey === true  → /auth/login  (use existing credential)
//   - hasPasskey === false → /auth/passkey (enroll a new credential)
// Both upgrade the session factor to passkey on success. Routing to
// /auth/passkey when a credential already exists is what trapped
// operators on the dead-end enrollment screen with the cryptic
// InvalidStateError (#1272).
//
// Returns the redirect path on a hit (already URL-encoded ?next=),
// or null when the navigation is allowed through. Pure function so
// the router guard stays a one-liner and the truth table is unit-
// tested in shouldRequirePasskey.spec.ts.

// Factor is what the backend writes onto the JWT and surfaces on
// /api/admin/me. Passkey is the only steady-state factor accepted on
// data routes; magic_link is the wizard-stage factor; recovery is the
// break-glass factor. null means useCurrentAdmin saw a 401 (no auth
// at all): apiClient already redirects 401 to /auth/login so we
// fall through here to avoid racing against that.
//
// Widened to `string | null` at the public boundary because the
// useCurrentAdmin store types factor as a bare string (the API can
// surface future factor values without breaking the SPA build). The
// truth-table assertions inside still pin the three canonical values.
type Factor = string | null

// pathRequiresPasskey returns true for the routes the guard gates.
// We pin to the operator dashboard subtree only, matching the backend
// RequirePasskeyFactor placement. Extending the scope to other authed
// surfaces (e.g. a future /admin/* operator-only namespace) means
// updating this predicate AND the truth table in the spec.
//
// The boundary check (exact match OR a trailing slash) prevents a
// future /dashboards-public or /admin/dashboard-help from getting
// caught accidentally. Same fence shape shouldBounceSetupRoute uses.
function pathRequiresPasskey(path: string): boolean {
  if (path === '/dashboard') return true
  if (path.startsWith('/dashboard/')) return true
  return false
}

// hasPasskey defaults to false on undefined so a missing /me field
// (legacy backend, race against fetch) routes conservatively through
// enrollment — which still works for the no-credential case. The
// reverse default would silently mint an /auth/login bounce that
// fails because no credential exists.
export function shouldRequirePasskey(
  path: string,
  factor: Factor,
  hasPasskey: boolean = false,
): string | null {
  if (!pathRequiresPasskey(path)) return null
  if (factor === 'passkey') return null
  // The backend's RequirePasskeyFactor admits factor=demo (self-serve
  // sales-demo session), and the demo visitor is barred from passkey
  // enrollment server-side — bouncing it to /auth/passkey is a dead
  // end (enrollment 403s). Mirror the backend and let demo through.
  if (factor === 'demo') return null
  if (factor === null) return null
  if (hasPasskey) return `/auth/login?next=${encodeURIComponent(path)}`
  return `/auth/passkey?next=${encodeURIComponent(path)}`
}
